import React from 'react';
import { Plane, Calendar, Users, Globe } from 'lucide-react';
import { fleetData } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const Fleet = () => {
  return (
    <div className="fleet-page">
      <SEO 
        title="Our Aircraft Fleet"
        description="Modern fleet of Boeing, Airbus, Embraer, and De Havilland aircraft available for lease. Including 737, 777, 787, A320, A330, A340, E-Jets, and Dash 8 aircraft."
        keywords="Boeing 737 lease, Airbus A320 lease, aircraft fleet, Boeing 787 lease, Embraer lease, De Havilland Dash 8"
        canonicalUrl="/fleet"
      />
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-title">Our Fleet</h1>
          <p className="page-subtitle">
            Modern, fuel-efficient aircraft powering African aviation
          </p>
        </div>
      </section>

      {/* Fleet Composition */}
      <section className="fleet-composition-section">
        <div className="fleet-composition-header">
          <h2 className="section-title">Fleet Composition</h2>
          <p className="section-subtitle">
            A diversified portfolio of modern commercial aircraft and engines
          </p>
        </div>

        <div className="fleet-types-grid">
          {fleetData.fleetTypes.map((fleet, index) => (
            <Card key={index} className="fleet-type-card">
              <CardContent className="fleet-type-content">
                <div className="fleet-type-header">
                  <h3 className="fleet-type-title">{fleet.type}</h3>
                </div>
                <p className="fleet-type-description">{fleet.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Aircraft Details by Manufacturer */}
      <section className="aircraft-details-section">
        <div className="aircraft-details-header">
          <h2 className="section-title">Fleet by Manufacturer</h2>
        </div>

        <div className="aircraft-details-container">
          {/* Boeing */}
          <div className="aircraft-detail-block">
            <div className="aircraft-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_0ccb6f97-1bfb-4ee2-9332-dc85509860bf/artifacts/lqcivfxj_B737%20Ramp.jpeg"
                alt="Boeing 737 on ramp"
                className="aircraft-detail-image"
              />
            </div>
            <div className="aircraft-detail-content">
              <h3 className="aircraft-detail-title">Boeing</h3>
              <p className="aircraft-detail-description">
                Our Boeing portfolio features the industry-leading 737 Family, advanced 787 Dreamliner, proven 777, and iconic 747. From regional operations with the 737 variants to long-haul intercontinental routes with wide-body aircraft, our Boeing fleet delivers exceptional economics, fuel efficiency, and passenger comfort across diverse operational needs.
              </p>
              <div className="aircraft-specs">
                <div className="aircraft-spec-item">
                  <span className="spec-label">Aircraft Types:</span>
                  <span className="spec-value">Boeing 737, 737NG, 737 Max, 787, 777, and 747</span>
                </div>
                <div className="aircraft-spec-item">
                  <span className="spec-label">Capacity Range:</span>
                  <span className="spec-value">160-400+ passengers</span>
                </div>
                <div className="aircraft-spec-item">
                  <span className="spec-label">Engines:</span>
                  <span className="spec-value">CFM56-7B, LEAP-1B, GEnx, GE90, Trent 1000</span>
                </div>
              </div>
            </div>
          </div>

          {/* Airbus */}
          <div className="aircraft-detail-block aircraft-detail-reverse">
            <div className="aircraft-detail-content">
              <h3 className="aircraft-detail-title">Airbus</h3>
              <p className="aircraft-detail-description">
                The Airbus fleet represents our largest manufacturer portfolio spanning the complete A320 Family and wide-body aircraft. From the compact A318 to the versatile A319 and A320, through to long-haul operations with the A330 and A340, our Airbus portfolio provides proven reliability, fuel efficiency, and operational flexibility across short-haul, medium-haul, and intercontinental routes.
              </p>
              <div className="aircraft-specs">
                <div className="aircraft-spec-item">
                  <span className="spec-label">Aircraft Types:</span>
                  <span className="spec-value">A318, A319, A320, A330 and A340</span>
                </div>
                <div className="aircraft-spec-item">
                  <span className="spec-label">Capacity Range:</span>
                  <span className="spec-value">107-380 passengers</span>
                </div>
                <div className="aircraft-spec-item">
                  <span className="spec-label">Engines:</span>
                  <span className="spec-value">CFM56-5B, LEAP-1A, Trent 500, Trent 700</span>
                </div>
              </div>
            </div>
            <div className="aircraft-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_0ccb6f97-1bfb-4ee2-9332-dc85509860bf/artifacts/1uwkq5ax_A320%20Interior%201.jpeg"
                alt="Airbus A320 interior"
                className="aircraft-detail-image"
              />
            </div>
          </div>

          {/* Embraer */}
          <div className="aircraft-detail-block">
            <div className="aircraft-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_0ccb6f97-1bfb-4ee2-9332-dc85509860bf/artifacts/3p8o7z14_B737%20Cockpit.jpeg"
                alt="Aircraft cockpit"
                className="aircraft-detail-image"
              />
            </div>
            <div className="aircraft-detail-content">
              <h3 className="aircraft-detail-title">Embraer</h3>
              <p className="aircraft-detail-description">
                Our Embraer E-Jet family provides optimal solutions for regional and thin route operations. The E175, E190, and next-generation E195-E2 deliver jet comfort and economics perfectly sized for African regional markets, connecting smaller cities with major hubs efficiently.
              </p>
              <div className="aircraft-specs">
                <div className="aircraft-spec-item">
                  <span className="spec-label">Aircraft Types:</span>
                  <span className="spec-value">E175, E190, E195-E2</span>
                </div>
                <div className="aircraft-spec-item">
                  <span className="spec-label">Capacity Range:</span>
                  <span className="spec-value">76-146 passengers</span>
                </div>
                <div className="aircraft-spec-item">
                  <span className="spec-label">Range:</span>
                  <span className="spec-value">2,200 - 2,600 NM</span>
                </div>
              </div>
            </div>
          </div>

          {/* De Havilland */}
          <div className="aircraft-detail-block aircraft-detail-reverse">
            <div className="aircraft-detail-content">
              <h3 className="aircraft-detail-title">De Havilland</h3>
              <p className="aircraft-detail-description">
                The De Havilland Dash 8-400 turboprop serves as the workhorse for short-haul regional routes and remote community access. With exceptional fuel efficiency, low operating costs, and the ability to operate from shorter runways, these aircraft enable connectivity to underserved markets across the African continent.
              </p>
              <div className="aircraft-specs">
                <div className="aircraft-spec-item">
                  <span className="spec-label">Aircraft Type:</span>
                  <span className="spec-value">Dash 8-400 (Q400)</span>
                </div>
                <div className="aircraft-spec-item">
                  <span className="spec-label">Capacity:</span>
                  <span className="spec-value">68-78 passengers</span>
                </div>
                <div className="aircraft-spec-item">
                  <span className="spec-label">Range:</span>
                  <span className="spec-value">1,200 NM</span>
                </div>
              </div>
            </div>
            <div className="aircraft-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_0ccb6f97-1bfb-4ee2-9332-dc85509860bf/artifacts/lqcivfxj_B737%20Ramp.jpeg"
                alt="Regional aircraft"
                className="aircraft-detail-image"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Fleet;